import { LinkExpiredAlert, UnexpectedErrorAlert } from '@/components/alerts';
import { PasswordInput } from '@/components/PasswordInput';
import { PasswordRulesPopover } from '@/components/PasswordRulesPopover';
import { SubmitButton } from '@/components/SubmitButton';
import { AccountCenterBox } from '@/layout/AccountCenterBox';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { api } from '@/services/api';
import { ResetPasswordResponse, ResetPasswordResultEnum } from '@shared/types';
import { AxiosResponse } from 'axios';
import { Field, Form, Formik } from 'formik';
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import { ValidateActivationTokenResultEnum } from '@shared/types';

enum FormStatus {
  NoAlert = 'NoAlert',
  LinkExpiredAlert = 'LinkExpiredAlert',
  UnexpectedError = 'UnexpectedErrorAlert',
}
enum PageState {
  Loading = 'Loading',
  Valid = 'Valid',
  Invalid = 'Invalid',
}

export const ResetPassword = () => {
  useDocumentTitle('Marshall Reddick - Reset Password');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = decodeURIComponent(searchParams.get('token') ?? '');
  const email = decodeURIComponent(searchParams.get('email') ?? '');

  const [pageState, setPageState] = useState<PageState>(PageState.Loading);
  const [isPasswordValid, setIsPasswordValid] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      if (!token || !email) {
        setPageState(PageState.Invalid);
        return;
      }
      try {
        const response = await api.get('/auth/reset-password', {
          params: { token, email },
        });
        if (response.data.result === ValidateActivationTokenResultEnum.VALID) {
          setPageState(PageState.Valid);
        } else {
          setPageState(PageState.Invalid);
        }
      } catch {
        setPageState(PageState.Invalid);
      }
    };
    validateToken();
  }, [token, email]);

  if (pageState === PageState.Loading) {
    return (
      <AccountCenterBox title="Set a new password" isSubmitting={true}>
        <div className="text-center py-8">
          <p>Validating your reset link...</p>
        </div>
      </AccountCenterBox>
    );
  }

  if (pageState === PageState.Invalid) {
    return (
      <AccountCenterBox title="Set a new password" isSubmitting={false}>
        <LinkExpiredAlert />
      </AccountCenterBox>
    );
  }
  const alertMap: Partial<Record<FormStatus, React.ReactNode>> = {
    [FormStatus.LinkExpiredAlert]: <LinkExpiredAlert />,
    [FormStatus.UnexpectedError]: <UnexpectedErrorAlert />,
  };

  return (
    <Formik
      initialValues={{ password: '', passwordConfirmation: '' }}
      initialStatus={FormStatus.NoAlert}
      validationSchema={Yup.object().shape({
        password: Yup.string(),
        passwordConfirmation: Yup.string(),
      })}
      validateOnMount={false}
      onSubmit={async (values, { setFieldError, setStatus }) => {
        if (!values.password.trim()) {
          setFieldError('password', 'Password is required');
        }
        if (!values.password.trim() || !isPasswordValid) {
          return;
        }
        setStatus(FormStatus.NoAlert);

        try {
          const response: AxiosResponse<ResetPasswordResponse> = await api.post(
            '/auth/reset-password',
            {
              password: values.password,
              token,
              email,
            },
          );

          const { result } = response.data;
          if (result === ResetPasswordResultEnum.SUCCESS) {
            setStatus(FormStatus.NoAlert);
            navigate('/login', { state: { status: 'PasswordResetSuccessful' } });
          } else if (result === ResetPasswordResultEnum.LINK_EXPIRED) {
            setStatus(FormStatus.LinkExpiredAlert);
          } else {
            setStatus(FormStatus.UnexpectedError);
          }
        } catch {
          setStatus(FormStatus.UnexpectedError);
        }
      }}
    >
      {({ errors, touched, values, isSubmitting, status }) => {
        const statusKey = status as FormStatus;
        return (
          <AccountCenterBox title="Set a new password" isSubmitting={isSubmitting}>
            <Form noValidate>
              {statusKey in alertMap ? alertMap[statusKey] : null}
              {/* PASSWORD */}
              <Field
                name="password"
                as={PasswordInput}
                error={errors.password}
                touched={touched.password}
              />
              {/* PASSWORD CONFIRMATION */}
              <Field
                name="passwordConfirmation"
                label="Confirm Password"
                as={PasswordInput}
                error={errors.passwordConfirmation}
                touched={touched.passwordConfirmation}
              />
              <PasswordRulesPopover
                password={values.password}
                confirm={values.passwordConfirmation}
                onValidityChange={setIsPasswordValid}
              />
              {/* BUTTON */}
              <SubmitButton
                text="Save Password"
                isSubmitting={isSubmitting}
                disabled={!isPasswordValid}
              />
            </Form>
          </AccountCenterBox>
        );
      }}
    </Formik>
  );
};
